/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect } from "react";
import FormFader from "../Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import Hidden from "@mui/material/Hidden";
import EnforceIcon from "../../../assets/icons/contract/toggleable/IconEnforce";
import DefendIcon from "../../../assets/icons/contract/toggleable/IconDefend";
import AdvisoryIcon from "../../../assets/icons/contract/toggleable/IconQuestionmarkBubble";
import Person from "../Common/Person/Person";
import _includes from "lodash/includes";
import Address from "../Common/Address/Address";
import PersonIcon from "../../../assets/icons/provision/toggleable/IconPerson";
import OtherPersonIcon from "../../../assets/icons/contract/toggleable/IconMultiplePeople";
import FamilyIcon from "../../../assets/icons/generic/toggleable/IconFamilyDetailed";
import ContractBaseEnforceClaim from "./ContractBaseEnforceClaim";
import ContractBaseDefendClaim from "./ContractBaseDefendClaim";
import {
  dateFieldDefault,
  isLessThen18YearsAgo,
  isPhoneNumber,
  minStringLength,
  pastOrSameDateDefault,
  requiredFieldDefault,
  requiredValidator,
} from "../../../services/validationRules";
import { useForm } from "../../../provider/Form/FormProvider";
import Group from "../../Wizard/Group";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import FormLegend from "../Common/FormLegend/FormLegend";
import ValidatorDateField from "../../FormFields/ValidatorDateField";
import HighlightBox from "../../Box/HighlightBox/HighlightBox";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import AdditionalClientsForm from "../Common/AdditionalClientsForm/AdditionalClientsForm";
import FormSubmitProps from "../../../types/FormSubmitProps";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import { translate } from "../../../services/translations/translations";
import wizardStyle from "../wizardStyle";
import { Box } from "@mui/material";
import QuestionMarkIcon from "../../../assets/icons/common/toggleable/IconQuestionmark";

type ContractBaseProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
  hideCaseCategories: boolean;
  alternativeHeadline?: string;
};

export default function ContractBase({ submitProps, product, hideCaseCategories = false, alternativeHeadline }: ContractBaseProps) {
  const { values, handleSubmit, handleChange } = useForm();

  useEffect(() => {
    if (values.legalRepresentativeType === "me") {
      handleChange({
        target: {
          name: "clientLegalRepresentative.gender",
          value: values.customer.person.gender,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.givenName",
          value: values.customer.person.givenName,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.familyName",
          value: values.customer.person.familyName,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.streetAddress",
          value: values.customer.person.residenceAddress.streetAddress,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.postalCode",
          value: values.customer.person.residenceAddress.postalCode,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.addressLocality",
          value: values.customer.person.residenceAddress.addressLocality,
          type: "text",
        },
      });
      return;
    }
    handleChange({
      target: {
        name: "clientLegalRepresentative.gender",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.givenName",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.familyName",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.streetAddress",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.postalCode",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.addressLocality",
        value: "",
        type: "text",
      },
    });
  }, [values.legalRepresentativeType]);

  useEffect(() => {
    if (values.affectedPartyType !== "coinsured") {
      handleChange({
        target: {
          name: "client.gender",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.givenName",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.familyName",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.birthDate",
          value: null,
        },
      });
      handleChange({
        target: {
          name: "clientWasMinorAtContractDate",
          value: null,
        },
      });
      return;
    }
  }, [values.affectedPartyType]);

  useEffect(() => {
    if (values.affectedPartyType !== "customer" || values.howManyClients === "oneClient") {
      handleChange({
        target: {
          name: "additionalClients",
          value: [],
        },
      });
      return;
    }
    if (values.howManyClients === "twoClients" && !values.additionalClients?.length) {
      handleChange({
        target: {
          name: "additionalClients",
          value: product.additionalClients?.length
            ? product.additionalClients
            : [
                {
                  givenName: "",
                  familyName: "",
                  gender: "",
                },
              ],
        },
      });
    }
  }, [values.howManyClients, values.affectedPartyType]);

  const getCaseCategoryOptions = () => {
    let options = [
      {
        labelText: "Ich möchte eine Forderung durchsetzen",
        labelIcon: <EnforceIcon />,
        labelIconActive: <EnforceIcon active />,
        value: "enforceClaim",
        optionHintText:
          "Der Vertrag wurde nicht wie vereinbart erfüllt und ich möchte dies nun einfordern oder Geld zurück verlangen. Beispiele: Bei einem Kauf wurde die Ware defekt oder gar nicht geliefert. | Bei einem Verkauf wurde der Kaufpreis nicht gezahlt.",
      },
      {
        labelText: "Ich möchte mich gegen eine Forderung wehren",
        labelIcon: <DefendIcon />,
        labelIconActive: <DefendIcon active />,
        value: "defendClaim",
        optionHintText:
          "Von mir wird unberechtigterweise etwas eingefordert und ich möchte mich dagegen wehren. Beispiele: Ich bin von einem Vertrag zurück getreten und trotzdem wird Geld verlangt. | Ich habe nie einen Vertrag geschlossen, soll jetzt aber etwas zahlen. ",
      },
    ];
    if (product.categoryChange === null || product.categoryChange.previousCaseCategory !== "advisory") {
      options = [
        {
          labelText: "Ich benötige zunächst nur eine Erstberatung",
          labelIcon: <AdvisoryIcon />,
          labelIconActive: <AdvisoryIcon active />,
          value: "advisory",
          optionHintText:
            "Ich möchte nichts weiter veranlassen, sondern habe nur eine kurze Frage im Bereich Vertragsrecht und benötige hierfür eine Erstberatung.",
        },
        ...options,
      ];
    }
    return options;
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Hidden smUp>
            <Typography variant={"h4"} sx={wizardStyle.headline}>
              Ihr Vertrag
            </Typography>
          </Hidden>
          <Grid container justifyContent={"flex-start"} alignItems={"baseline"}>
            <Grid item xs={12}>
              <FormLegend>
                <strong>{alternativeHeadline !== null ? alternativeHeadline : "Allgemeines zu Ihrem Vertrag"}</strong>
              </FormLegend>
            </Grid>
            {!hideCaseCategories && (
              <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                <Group
                  type="radio"
                  name="caseCategory"
                  question="Wie können wir Ihnen weiterhelfen?"
                  options={getCaseCategoryOptions()}
                  validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                />
              </Grid>
            )}
            <Person path="customer.person" headline={"Wie heißen Sie?"} showDiverseGenderOption />
            {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && <Address path="customer.person.residenceAddress" headline={"Wo wohnen Sie?"} />}
            <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
              <FormLegend>Wie können wir Sie erreichen?</FormLegend>
            </Grid>
            <Grid item xs={12}>
              <ValidatorTextField
                name="customer.telephone"
                label="Ihre Telefonnummer"
                autoComplete="tel"
                validators={[
                  requiredValidator("Bitte geben Sie Ihre Telefonnummer an"),
                  {
                    validator: minStringLength,
                    params: [7],
                    message: "Ihre Telefonnummer ist zu kurz",
                  },
                  { validator: isPhoneNumber, message: "Ihre Telefonnummer enthält ungültige Zeichen" },
                ]}
              />
            </Grid>
            {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && (
              <>
                <Grid item xs={12}>
                  <Group
                    type="radio"
                    name="affectedPartyType"
                    question={"Wer hat den Vertrag geschlossen, um den es bei Ihrem Anliegen geht?"}
                    options={[
                      {
                        labelText: "Ich habe den Vertrag selbst abgeschlossen",
                        labelIcon: <PersonIcon />,
                        labelIconActive: <PersonIcon active />,
                        value: "customer",
                      },
                      {
                        labelText: "Der Vertrag wurde von jemand anderem abgeschlossen, der mir bekannt ist - zum Beispiel ein Familienmitglied",
                        labelIcon: <FamilyIcon />,
                        labelIconActive: <FamilyIcon active />,
                        value: "coinsured",
                      },
                      {
                        labelText: "Ich weiß nicht, wer den Vertrag abgeschlossen hat",
                        labelIcon: <QuestionMarkIcon />,
                        labelIconActive: <QuestionMarkIcon active />,
                        value: "unknown",
                      },
                    ]}
                    validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                  />
                </Grid>

                {values.affectedPartyType === "customer" && <AdditionalClientsForm product={product} />}
                {values.affectedPartyType === "coinsured" && (
                  <Grid item xs={12}>
                    <Group
                      type="radio"
                      name="clientWasMinorAtContractDate"
                      question={"Ist die Person zum gegenwärtigen Zeitpunkt minderjährig?"}
                      options={[
                        {
                          labelText: "Ja",
                          labelIcon: <CheckIcon />,
                          labelIconActive: <CheckIcon active />,
                          value: "yes",
                        },
                        {
                          labelText: "Nein",
                          labelIcon: <CrossIcon />,
                          labelIconActive: <CrossIcon active />,
                          value: "no",
                        },
                      ]}
                      validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                    />
                  </Grid>
                )}
                {values.affectedPartyType === "unknown" && (
                  <HighlightBox>
                    Auch wenn Ihnen nicht bekannt ist, wer den Vertrag geschlossen hat, machen Sie bitte in den folgenden Schritten alle Angaben so, als ob Sie
                    den Vertrag geschlossen hätten, damit uns möglichst viele Informationen zum Fall vorliegen. Stören Sie sich dabei nicht an einzelnen
                    Formulierungen: Sie haben am Ende des Formulars die Möglichkeit, Ihren Fall frei und in eigenen Worten zu schildern.
                  </HighlightBox>
                )}
                {values.affectedPartyType === "coinsured" && (
                  <>
                    <Person path="client" headline="Wie heißt die Person, die den Vertrag geschlossen hat?" showDiverseGenderOption />
                    {values.clientWasMinorAtContractDate === "yes" && (
                      <Grid item xs={12}>
                        <ValidatorDateField
                          name="client.birthDate"
                          label="Geburtstag Vertragsschließender"
                          validators={[
                            requiredFieldDefault,
                            dateFieldDefault,
                            pastOrSameDateDefault,
                            {
                              validator: isLessThen18YearsAgo,
                              message: "Die Person ist bereits volljährig. Bitte Datum anpassen oder vorherige Frage mit “Nein” beantworten",
                            },
                          ]}
                        />
                      </Grid>
                    )}
                    <Address path="client.residenceAddress" headline={"Wo wohnt diese Person?"} />
                    {values.clientWasMinorAtContractDate === "yes" && (
                      <>
                        <Grid item xs={12}>
                          <Group
                            type="radio"
                            name="clientLegalRepresentativeAmount"
                            modalContent={
                              <>
                                <Typography paragraph>
                                  Wenn die Person, die den Vertrag geschlossen hat, zu diesem Zeitpunkt minderjährig war, müssen die gesetzlichen Vertreter
                                  Legalbird eine Vollmacht zur rechtlichen Vertretung des Minderjährigen erteilen.
                                </Typography>
                                <Typography paragraph>
                                  Als Eltern sind Sie Ihrem Kind gegenüber in der Regel gesamtvertretungsberechtigt. Das heißt, beide Elternteile müssen
                                  gemeinsam die Zustimmung erteilen. Sollte ein Elternteil verstorben sein oder ihm wurde das Sorgerecht entzogen, tragen Sie
                                  bitte nur das andere Elternteil als gesetzliche Vertretungsperson ein. Wenn Sie vom anderen Elternteil getrennt leben und
                                  beide einzelberechtigt sind, wird am Ende nur die Unterschrift der Person abgefragt, die als erster gesetzlicher Vertreter
                                  eingetragen ist.
                                </Typography>
                              </>
                            }
                            question={translate(`${product.name}.clientLegalRepresentativeAmount.question`, values)}
                            options={[
                              {
                                labelText: translate(`${product.name}.clientLegalRepresentativeAmount.options.1.label`, values),
                                labelIcon: <PersonIcon />,
                                labelIconActive: <PersonIcon active />,
                                value: "1",
                              },
                              {
                                labelText: translate(`${product.name}.clientLegalRepresentativeAmount.options.2.label`, values),
                                labelIcon: <OtherPersonIcon />,
                                labelIconActive: <OtherPersonIcon active />,
                                value: "2",
                              },
                            ]}
                            validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Group
                            type="radio"
                            name="legalRepresentativeType"
                            question={translate(`${product.name}.legalRepresentativeType.question`, values)}
                            options={[
                              {
                                labelText: translate(`${product.name}.legalRepresentativeType.options.me.label`, values),
                                labelIcon: values.clientLegalRepresentativeAmount == "2" ? <CheckIcon /> : <PersonIcon />,
                                labelIconActive: values.clientLegalRepresentativeAmount == "2" ? <CheckIcon active /> : <PersonIcon active />,
                                value: "me",
                              },
                              {
                                labelText: translate(`${product.name}.legalRepresentativeType.options.other.label`, values),
                                labelIcon: values.clientLegalRepresentativeAmount == 2 ? <CrossIcon /> : <OtherPersonIcon />,
                                labelIconActive: values.clientLegalRepresentativeAmount == 2 ? <CrossIcon active /> : <OtherPersonIcon active />,
                                value: "other",
                              },
                            ]}
                            validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                          />
                        </Grid>
                        <Person path="clientLegalRepresentative" headline={translate(`${product.name}.clientLegalRepresentative.question`, values)} />
                        <Address
                          path="clientLegalRepresentative.residenceAddress"
                          headline={translate(`${product.name}.clientLegalRepresentative.residenceAddress.question`, values)}
                        />
                        {values.clientLegalRepresentativeAmount === "2" && (
                          <>
                            <Person
                              path="secondClientLegalRepresentative"
                              headline={translate(`${product.name}.secondClientLegalRepresentative.question`, values)}
                            />
                            <Address
                              path="secondClientLegalRepresentative.residenceAddress"
                              headline={translate(`${product.name}.secondClientLegalRepresentative.residenceAddress.question`, values)}
                            />

                            <Grid item xs={12}>
                              <Group
                                type="radio"
                                name="clientLegalRepresentativesAuthorizationType"
                                question={translate(`${product.name}.clientLegalRepresentativesAuthorizationType.question`, values)}
                                options={[
                                  {
                                    labelText: translate(`${product.name}.clientLegalRepresentativesAuthorizationType.options.both.label`, values),
                                    labelIcon: <OtherPersonIcon />,
                                    labelIconActive: <OtherPersonIcon active />,
                                    value: "both",
                                  },
                                  {
                                    labelText: translate(`${product.name}.clientLegalRepresentativesAuthorizationType.options.individually.label`, values),
                                    labelIcon: <PersonIcon />,
                                    labelIconActive: <PersonIcon active />,
                                    value: "individually",
                                  },
                                ]}
                                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                              />
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
                <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                  <FormLegend>
                    Wann wurde der Vertrag abgeschlossen bzw. die Ware/Leistung gekauft? Wenn Sie den Tag nicht genau benennen können, oder der Meinung sind,
                    dass gar kein Vertrag zustande gekommen ist, geben Sie einfach ein ungefähres Datum ein.
                  </FormLegend>
                </Grid>
                <Grid item xs={12}>
                  <ValidatorDateField
                    name="contractDate"
                    label="Datum des Vertrags"
                    validators={[requiredValidator("Bitte geben Sie das Vertragsdatum an"), dateFieldDefault, pastOrSameDateDefault]}
                  />
                </Grid>
              </>
            )}
            {values.caseCategory === "enforceClaim" && <ContractBaseEnforceClaim product={product} />}
            {values.caseCategory === "defendClaim" && <ContractBaseDefendClaim product={product} />}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
